import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

import AlertList from '../../components/AlertPage/AlertList';
import AlertForm from '../../components/AlertPage/AlertForm';
import AlertDetail from '../../components/AlertPage/AlertDetail';
import AlertHistoryList from '../../components/AlertPage/AlertHistoryList';
import MonitoringDashboard from '../../components/AlertPage/MonitoringDashboard';

export default function AlertPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list', 'create', 'edit', 'detail', 'history', 'dashboard'
  const [selectedAlert, setSelectedAlert] = useState(null);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/alerts');
      setAlerts(response.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlert = () => {
    setSelectedAlert(null);
    setView('create');
  };

  const handleEditAlert = (alert) => {
    setSelectedAlert(alert);
    setView('edit');
  };

  const handleViewAlert = (alert) => {
    setSelectedAlert(alert);
    setView('detail');
  };

  const handleViewList = (alert) => {
    setSelectedAlert(alert);
    setView('list');
  };

  const handleViewHistory = (alert = null) => {
    setSelectedAlert(alert);
    setView('history');
  };

  const handleViewDashboard = () => {
    setView('dashboard');
  };

  const handleSaveAlert = () => {
    fetchAlerts();
    setView('list');
  };

  const handleCancel = () => {
    setView('list');
  };

  const renderNavigation = () => {
    // Helper function to determine button styles based on current view
    const getButtonStyles = (buttonView) => {
      const isActive = buttonView === view;
        
      return isActive
        ? "bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
        : "text-gray-600 hover:bg-gray-200 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline";
    };

    return (
      <div className="flex justify-between items-center mb-4">
        <div className="space-x-2">
          <button
            onClick={handleCreateAlert}
            className={getButtonStyles('create')}
          >
            Create New Alert
          </button>
          <button
            onClick={handleViewList}
            className={getButtonStyles('list')}
          >
            View All Alerts
          </button>
          <button
            onClick={() => handleViewHistory()}
            className={getButtonStyles('history')}
          >
            View System History
          </button>
          <button
            onClick={handleViewDashboard}
            className={getButtonStyles('dashboard')}
          >
            Monitoring Dashboard
          </button>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (view) {
      case 'create':
      case 'edit':
        return (
          <AlertForm 
            alert={selectedAlert} 
            onSave={handleSaveAlert} 
            onCancel={handleCancel} 
          />
        );
      case 'detail':
        return (
          <AlertDetail 
            alert={selectedAlert} 
            onEdit={handleEditAlert} 
            onClose={handleCancel} 
          />
        );
      case 'history':
        return (
            <AlertHistoryList alertId={selectedAlert?.id} />
        );
      case 'dashboard':
        return (
            <MonitoringDashboard />
        );
      case 'list':
      default:
        return (
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-8">Loading alerts...</div>
            ) : (
              <AlertList 
                alerts={alerts} 
                onEdit={handleEditAlert} 
                onView={handleViewAlert} 
                onRefresh={fetchAlerts} 
              />
            )}
          </div>
        );
    }
  };

  return (
    <div className="p-5 mb-10 ml-5 bg-white shadow text-gray-700">
      {renderNavigation()}
      {renderContent()}
    </div>
  );
} 